import type {
    ComposableOptionsBase,
    BundleSaveConfigRequest,
    SiteMembershipContext,
    Bundle
} from '~/types';
import type {
    PaginatedRequest,
    PaginatedPayload
} from 'x1-base/types';
import {
    useGenericCrudMethods,
    useCustomFetch
} from '~/composables';
import type { NitroFetchOptions } from 'nitropack';
import { BundleSaveConfigLegacyModel } from '~/models';

export interface UseBundlesOptions extends ComposableOptionsBase {
    siteMembershipContext?: SiteMembershipContext;
}
export function useBundles(options: Partial<UseBundlesOptions> = {}) {
    const apiBasePath = '/v1/bundles';
    const $_fetch = useCustomFetch({ siteContext: options.siteContext });
    const genericCrudMethods = useGenericCrudMethods<Partial<Bundle>>(
        apiBasePath,
        BundleSaveConfigLegacyModel,
        void(0)
    );

    async function getBundles(
        args: PaginatedRequest
    ): Promise<PaginatedPayload<Bundle> | Error> {
        return genericCrudMethods.getItems(args) as Promise<PaginatedPayload<Bundle> | Error>;
    }

    async function getBundle(uid: string): Promise<Bundle | Error> {
        return genericCrudMethods.getItem(uid) as Promise<Bundle | Error>;
    }

    async function saveBundleConfig(
        bundle: BundleSaveConfigRequest
    ): Promise<Bundle> {
        const url = `${apiBasePath}/`;

        const config: NitroFetchOptions<string> = {
            method: 'POST',
            body: bundle
        };

        return await $_fetch<Bundle>(url, config);
    }

    async function renameBundleConfig(uid: string, name: string): Promise<Bundle> {
        const url = `${apiBasePath}/${uid}/`;

        const config: NitroFetchOptions<string> = {
            method: 'PATCH',
            body: {
                name
            }
        };

        return await $_fetch<Bundle>(url, config);
    }

    async function deleteBundleConfig(uid: string): Promise<void> {
        const url = `${apiBasePath}/${uid}/`;

        const config: NitroFetchOptions<string> = {
            method: 'DELETE'
        };

        return await $_fetch<void>(url, config);
    }

    return {
        ...genericCrudMethods,
        getBundles,
        getBundle,
        saveBundleConfig,
        renameBundleConfig,
        deleteBundleConfig
    };
}
